import React, { useEffect, useState } from 'react';
import { adminApi, categoryApi, uploadApi } from '../../api';
import { Category, HomeBanner } from '../../types';
import Loading from '../../components/common/Loading';
import { toImageUrl } from '../../utils/image';

interface FormState {
  imageUrl: string | null;
  categoryId: number | null;
  displayOrder: number;
  enabled: boolean;
}

const emptyForm: FormState = { imageUrl: null, categoryId: null, displayOrder: 0, enabled: true };

const AdminBanner: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState<HomeBanner[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [b, c] = await Promise.all([adminApi.getHomeBanners(), categoryApi.getAll()]);
    setBanners(b.data.data);
    setCategories(c.data.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const categoryName = (id: number | null) =>
    id ? (categories.find(c => c.id === id)?.name ?? `#${id}`) : '연결 안 함 (전체 상품)';

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyForm, displayOrder: banners.length });
    setShowForm(true);
  };

  const openEdit = (b: HomeBanner) => {
    setEditId(b.id);
    setForm({ imageUrl: b.imageUrl, categoryId: b.categoryId, displayOrder: b.displayOrder, enabled: b.enabled });
    setShowForm(true);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadApi.image(file);
      setForm(f => ({ ...f, imageUrl: res.data.data }));
    } catch (err: any) {
      alert(err.response?.data?.message || '이미지 업로드 실패');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.imageUrl) { alert('배너 이미지를 업로드해주세요.'); return; }
    setSaving(true);
    try {
      if (editId) {
        await adminApi.updateHomeBanner(editId, form);
      } else {
        await adminApi.createHomeBanner(form);
      }
      setShowForm(false);
      await load();
    } catch (err: any) {
      alert(err.response?.data?.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('이 배너를 삭제하시겠습니까?')) return;
    await adminApi.deleteHomeBanner(id);
    await load();
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>배너 관리</h2>
        <button style={styles.addBtn} onClick={openCreate}>+ 배너 추가</button>
      </div>

      {banners.length === 0 ? (
        <div style={styles.empty}>
          <p>등록된 배너가 없습니다.</p>
          <button style={styles.addBtn} onClick={openCreate}>첫 배너 추가</button>
        </div>
      ) : (
        <div style={styles.list}>
          {banners.map(b => (
            <div key={b.id} style={styles.card}>
              <div style={styles.thumbWrap}>
                <img src={toImageUrl(b.imageUrl)} alt="" style={styles.thumb} />
              </div>
              <div style={styles.cardInfo}>
                <div style={styles.cardRow}>
                  <span style={styles.orderBadge}>순서 {b.displayOrder}</span>
                  {b.enabled
                    ? <span style={styles.onBadge}>노출중</span>
                    : <span style={styles.offBadge}>숨김</span>}
                </div>
                <p style={styles.linkText}>→ {categoryName(b.categoryId)}</p>
              </div>
              <div style={styles.cardBtns}>
                <button style={styles.editBtn} onClick={() => openEdit(b)}>수정</button>
                <button style={styles.delBtn} onClick={() => handleDelete(b.id)}>삭제</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p style={styles.hint}>홈에서는 순서(작은 값 먼저)대로 슬라이드되며, 노출중인 배너만 보입니다. 권장 사이즈 2400 × 800 px (비율 3:1).</p>

      {showForm && (
        <div style={styles.overlay}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>{editId ? '배너 수정' : '배너 추가'}</h3>

            <div style={styles.field}>
              <label style={styles.label}>배너 이미지</label>
              <div style={styles.preview}>
                {form.imageUrl
                  ? <img src={toImageUrl(form.imageUrl)} alt="" style={styles.previewImg} />
                  : <div style={styles.previewEmpty}>이미지 없음</div>}
              </div>
              <label style={styles.uploadBtn}>
                {uploading ? '업로드 중...' : '이미지 선택'}
                <input type="file" accept="image/*" onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
              </label>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>연결할 카테고리 (전문관)</label>
              <select
                style={styles.input}
                value={form.categoryId ?? ''}
                onChange={e => setForm({ ...form, categoryId: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">연결 안 함 (전체 상품 보기)</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>노출 순서 (작을수록 먼저)</label>
              <input
                type="number"
                style={styles.input}
                value={form.displayOrder}
                onChange={e => setForm({ ...form, displayOrder: Number(e.target.value) })}
              />
            </div>

            <label style={styles.checkboxRow}>
              <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />
              <span>배너 노출 (체크 해제 시 홈에서 숨김)</span>
            </label>

            <div style={styles.modalBtns}>
              <button style={styles.cancelBtn} onClick={() => setShowForm(false)} disabled={saving}>취소</button>
              <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700 },
  addBtn: { padding: '10px 20px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  empty: { padding: '60px 20px', textAlign: 'center', color: '#bbb', background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { display: 'flex', alignItems: 'center', gap: 16, background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', padding: 14 },
  thumbWrap: { width: 180, aspectRatio: '3 / 1', borderRadius: 8, overflow: 'hidden', background: '#fafafa', flexShrink: 0 },
  thumb: { width: '100%', height: '100%', objectFit: 'cover' },
  cardInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: 8 },
  cardRow: { display: 'flex', gap: 8, alignItems: 'center' },
  orderBadge: { fontSize: 12, color: '#666', background: '#f5f5f5', padding: '3px 10px', borderRadius: 10 },
  onBadge: { fontSize: 12, color: '#4caf50', background: '#e8f5e9', padding: '3px 10px', borderRadius: 10, fontWeight: 600 },
  offBadge: { fontSize: 12, color: '#999', background: '#f0f0f0', padding: '3px 10px', borderRadius: 10, fontWeight: 600 },
  linkText: { fontSize: 13, color: '#5c6bc0', margin: 0 },
  cardBtns: { display: 'flex', gap: 6 },
  editBtn: { padding: '6px 14px', background: '#ece3f7', color: '#b39ddb', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  delBtn: { padding: '6px 14px', background: '#fff0f0', color: '#e53935', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  hint: { fontSize: 12, color: '#999', marginTop: 16 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 },
  modalTitle: { fontSize: 18, fontWeight: 700 },
  field: { display: 'flex', flexDirection: 'column', gap: 8 },
  label: { fontSize: 14, fontWeight: 700, color: '#333' },
  preview: { width: '100%', aspectRatio: '3 / 1', background: '#fafafa', borderRadius: 10, border: '1px dashed #e0e0e0', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  previewImg: { width: '100%', height: '100%', objectFit: 'cover' },
  previewEmpty: { color: '#bbb', fontSize: 14 },
  uploadBtn: { alignSelf: 'flex-start', padding: '8px 18px', background: '#ece3f7', color: '#b39ddb', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  input: { padding: '11px 14px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, outline: 'none' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#333', cursor: 'pointer' },
  modalBtns: { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  cancelBtn: { padding: '10px 20px', border: '1px solid #e8e8e8', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 },
  saveBtn: { padding: '10px 24px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
};

export default AdminBanner;
